import { useEffect, useMemo } from 'react';
import { milestoneTree } from './milestoneTreeModel';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Building2, Link2, Lock } from 'lucide-react';
import { api } from '../../services/api';
import Field from '../ui/Field';
import EntityPicker from '../ui/EntityPicker';
import { withCurrent } from '../ui/entityPickerModel';
import { engagementLabel } from '../../utils/engagementLabel';
import { usePermissions } from '../../hooks/usePermissions';
import { pickableMilestones } from '../projects/taskFormModel';
import { CUSTOMERS_GROUP_KEY } from '../../utils/internalWorkOption';
import {
  autoPickChain, chainHasMilestone, internalOnlyChain, engagementsOfType, isLocked, openEngagements, pickCustomer, restrictToInternalWork,
  setChainField, typesWithOpenEngagement, withInternalWorkFirst, isCompanyChain, pickInternalProject, selectedInternalProject,
  type ChainEngagement, type ChainLock, type ChainValue, type InternalWorkOption, type InternalProjectChoice,
} from './engagementChainModel';

/**
 * P-02b (D-26, plan §6.1–§6.2, §6.6) — "where does it live": customer* →
 * engagement type* → engagement* → milestone (project / development only).
 * One hook (`useEngagementChain`: data + auto-pick) and one renderer
 * (`EngagementChainFields`), used by the task form, the new-ticket form, the
 * timer and "רישום שעות". The rules live in `engagementChainModel`.
 *
 * Customers: active only (D-30). Types: only those the customer has an open
 * engagement of. A locked level (filled from the screen) is shown as text
 * with a small lock, never as a picker.
 */

export interface EngagementChainOptions {
  value: ChainValue;
  onChange: (next: ChainValue) => void;
  lock?: ChainLock;
  /** Show the "type" level (the new-ticket form lists every type in one picker instead). */
  withType?: boolean;
  /** Show the milestone level on a project / development. */
  withMilestone?: boolean;
  /**
   * Ticket form: one picker with the customer's engagements of every type
   * (T-02) — open ones only, plus the ticket's own engagement even when it is
   * closed (P-02b-4 D6).
   */
  anyEngagement?: boolean;
  enabled?: boolean;
  /**
   * SEC-2b (D-106) — a plain employee opening a task for themself: every
   * customer with an OPEN engagement and every open engagement of it.
   */
  selfTask?: boolean;
}

export function useEngagementChain({ value, onChange, lock, withType = true, withMilestone = true, anyEngagement = false, enabled = true, selfTask = false }: EngagementChainOptions) {
  const { t } = useTranslation();
  // P-33c (D-185): an internal-only employee has no customer / engagement choice at all.
  const { internalOnly, internalWorkOrganizationId, internalWorkEngagementId } = usePermissions();
  const { data: orgsRes, isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations', selfTask ? 'self-task' : 'active-for-pick'],
    queryFn: (): Promise<{ data?: Array<{ id: string; name: string }> }> => (selfTask
      ? api.getSelfTaskCustomers()
      : api.getOrganizations({ status: 'active', limit: 500, sort: 'name' })),
    staleTime: 60_000,
    enabled: enabled && !internalOnly,
  });
  // P-33a (D-185 / D-188) — "OTE, עבודה פנימית": the first option of the
  // customer level (the company is never listed as a customer).
  const { data: internalRes, isLoading: internalLoading } = useQuery({
    queryKey: ['engagements', 'internal-work'],
    queryFn: () => api.getInternalWork(),
    staleTime: 5 * 60_000,
    enabled: enabled && !internalOnly,
  });
  // D-274 §3: an internal-only employee knows "עבודה פנימית" from /permissions/me.
  const internalWork: InternalWorkOption | null = internalOnly
    ? (internalWorkOrganizationId && internalWorkEngagementId
      ? { organizationId: internalWorkOrganizationId, engagementId: internalWorkEngagementId } : null)
    : internalRes?.data
      ? { organizationId: internalRes.data.organization_id, engagementId: internalRes.data.engagement_id }
      : null;
  // D-274 §3 — the task form on "עבודה פנימית OTE": "פרויקט (לא חובה)" (+ "אבן דרך
  // (לא חובה)") instead of type / engagement / milestone. A placement fixed by
  // the screen (an internal project's page) keeps the usual locked fields.
  // QA-D274 קל 1: a placement fixed by the screen (an internal project's page) shows the
  // same internal form, with the project locked (and a milestone only if there is one).
  const companyMode = withType && isCompanyChain(value, internalWork);
  const companyLocked = companyMode && isLocked('engagementId', lock);
  const { data: ipRes, isLoading: internalProjectsLoading } = useQuery({
    queryKey: ['engagements', 'internal-projects'],
    queryFn: () => api.getInternalProjects(),
    staleTime: 60_000,
    enabled: enabled && companyMode,
  });
  const internalProjects: InternalProjectChoice[] = (ipRes?.data ?? []) as InternalProjectChoice[];
  const internalProject = companyMode ? selectedInternalProject(value, internalProjects) : null;
  const organizations = withInternalWorkFirst(
    (orgsRes?.data ?? []) as Array<{ id: string; name: string }>, internalWork, t('engagementChain.internalWork'),
  );

  const { data: engRes, isLoading: engagementsLoading } = useQuery({
    queryKey: ['engagements', 'chain', value.organizationId, selfTask],
    queryFn: () => api.getEngagements({ organization_id: value.organizationId, limit: 200, ...(selfTask ? { for_self_task: true } : {}) }),
    staleTime: 30_000,
    // A locked internal project reads its name / project row from here (it may not be in the open list).
    enabled: enabled && !!value.organizationId && (companyLocked || (!internalOnly && !companyMode)),
  });
  const allEngagements = restrictToInternalWork(
    (engRes?.data ?? []) as unknown as ChainEngagement[], value.organizationId, internalWork,
    isLocked('engagementId', lock) ? value.engagementId : undefined,
  );
  const selectedEngagement = allEngagements.find((e) => e.id === value.engagementId) ?? null;

  const types = useMemo(() => {
    const open = typesWithOpenEngagement(allEngagements);
    // A locked / pre-filled engagement keeps its type in the list even when it is not open.
    if (selectedEngagement && !open.includes(selectedEngagement.type)) return [...open, selectedEngagement.type];
    return open;
  }, [allEngagements, selectedEngagement]);

  const engagementOptions = useMemo(() => {
    if (anyEngagement) return openEngagements(allEngagements, value.engagementId);
    if (!value.engagementType) return [];
    return engagementsOfType(allEngagements, value.engagementType, value.engagementId);
  }, [anyEngagement, allEngagements, value.engagementType, value.engagementId]);

  // A locked project that is not in the open list (or "עבודה פנימית" itself): its milestones from the API.
  const lockedOutsideList = companyLocked && !internalProject;
  const projectId = companyMode
    ? (internalProject?.project_id ?? (companyLocked ? selectedEngagement?.project_id : null) ?? internalRes?.data?.project_id ?? '')
    : (selectedEngagement?.project_id ?? '');
  const { data: msRes, isLoading: msLoading } = useQuery({
    // D-280: every milestone of the project (the 4 core ones too), not the project page's list
    // narrowed to the caller's own work — an employee may place a task on any of them.
    queryKey: ['milestones', projectId, 'task-choices'],
    queryFn: () => api.getMilestoneTaskChoices(projectId),
    staleTime: 60_000,
    enabled: enabled && !!projectId && withMilestone
      && (companyMode ? lockedOutsideList : !!selectedEngagement && chainHasMilestone(selectedEngagement.type)),
  });
  const milestones: Array<{ id: string; name: string; parent_milestone_id?: string | null }> = companyMode && !lockedOutsideList
    ? [...(internalProject?.milestones ?? [])]
    : pickableMilestones(msRes?.data ?? [], value.milestoneId);
  // D-274 §3: on "עבודה פנימית OTE" the milestone is offered only when the project has milestones.
  const showMilestone = companyMode
    ? withMilestone && (milestones.length > 0 || (isLocked('milestoneId', lock) && !!value.milestoneId))
    : withMilestone && !!selectedEngagement && chainHasMilestone(selectedEngagement.type);
  const milestonesLoading = companyMode && !lockedOutsideList ? internalProjectsLoading : msLoading;

  // A pre-filled engagement whose type was not given: take the type from the list (no reset below).
  useEffect(() => {
    if (selectedEngagement && withType && value.engagementType !== selectedEngagement.type) {
      onChange({ ...value, engagementType: selectedEngagement.type });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEngagement?.id, selectedEngagement?.type]);

  // P-33c: the fixed placement of an internal-only employee.
  const fixedChain = internalOnly ? internalOnlyChain(internalWorkOrganizationId, internalWorkEngagementId) : null;
  useEffect(() => {
    if (!enabled || !fixedChain) return;
    // D-274 §3: she may place the task on a project of "עבודה פנימית OTE" (and its milestone).
    if (value.organizationId === fixedChain.organizationId && value.engagementId) return;
    if (JSON.stringify(fixedChain) !== JSON.stringify(value)) onChange(fixedChain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fixedChain?.organizationId, fixedChain?.engagementId, value.organizationId, value.engagementId, value.engagementType, value.milestoneId]);

  // One option on a level = picked (plan §6.1).
  useEffect(() => {
    if (!enabled || internalOnly) return;
    const next = autoPickChain(value, {
      organizations: orgsLoading || internalLoading ? [] : organizations,
      types: !withType || engagementsLoading || companyMode ? [] : types,
      engagements: engagementsLoading || companyMode ? [] : engagementOptions,
    }, lock, withType);
    if (JSON.stringify(next) !== JSON.stringify(value)) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, orgsLoading, internalLoading, engagementsLoading, organizations.length, types.join(), engagementOptions.length, value.organizationId, value.engagementType, value.engagementId]);

  const organizationName = organizations.find((o) => o.id === value.organizationId)?.name
    ?? selectedEngagement?.organization_name ?? allEngagements[0]?.organization_name ?? '';

  return {
    value, onChange, lock, withType, anyEngagement,
    /** P-33c (D-185): no customer / engagement / type fields for an internal-only employee. */
    hidden: internalOnly,
    organizations, organizationName, orgsLoading, internalWork,
    /** D-274 §3 — "עבודה פנימית OTE": the project picker instead of type / engagement. */
    companyMode, companyLocked, internalProjects, internalProject, internalProjectsLoading,
    engagements: engagementOptions, engagementsLoading, selectedEngagement, types,
    showMilestone, milestones, milestonesLoading,
    /** The engagement's projects row (project / development), else ''. */
    projectId,
  };
}

export type EngagementChain = ReturnType<typeof useEngagementChain>;

/**
 * Where a form opened from a screen starts (plan §6.2). Any of the ids; the
 * missing upper levels are read from the project / milestone (react-query
 * keys `['project', id]` / `['milestone', id]`, usually already cached by the
 * page on screen).
 */
export interface TaskPlacement {
  organizationId?: string;
  engagementId?: string;
  engagementType?: string;
  projectId?: string;
  milestoneId?: string;
}

export function useResolvedPlacement(p: TaskPlacement | undefined): { ready: boolean; value: ChainValue } {
  const needsMilestone = !!p && !p.engagementId && !p.projectId && !!p.milestoneId;
  const { data: msRes, isFetched: msDone } = useQuery({
    queryKey: ['milestone', p?.milestoneId],
    queryFn: () => api.getMilestone(p!.milestoneId!),
    enabled: needsMilestone,
    staleTime: 60_000,
  });
  const projectId = p?.projectId || (needsMilestone ? ((msRes?.data as any)?.project_id ?? '') : '');
  // Only the engagement is known (the engagement page, the remembered one): read its customer.
  const needsEngagement = !!p && !!p.engagementId && !p.organizationId;
  const { data: engRes, isFetched: engDone } = useQuery({
    queryKey: ['engagement', p?.engagementId],
    queryFn: () => api.getEngagement(p!.engagementId!),
    enabled: needsEngagement,
    staleTime: 60_000,
  });
  const needsProject = !!p && !p.engagementId && !!projectId;
  const { data: projRes, isFetched: projDone } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
    enabled: needsProject,
    staleTime: 60_000,
  });
  const proj = projRes?.data as { organization_id?: string; engagement_id?: string | null } | undefined;
  const ready = !p
    || ((!needsMilestone || msDone) && (!needsProject || projDone) && (!needsEngagement || engDone));
  return {
    ready,
    value: {
      organizationId: p?.organizationId || engRes?.data?.organization_id || proj?.organization_id || (msRes?.data as any)?.organization_id || '',
      engagementType: p?.engagementType || engRes?.data?.type || '',
      engagementId: p?.engagementId || proj?.engagement_id || '',
      milestoneId: p?.milestoneId || '',
    },
  };
}

/** A level fixed by the screen: text with a small lock, no picker. */
export function LockedValue({ label, text, id }: { label: string; text: string; id: string }) {
  const { t } = useTranslation();
  return (
    <Field label={label} id={id}>
      <p className="flex min-h-touch items-center gap-1.5 text-sm text-text" aria-readonly="true">
        <Lock size={12} className="shrink-0 text-text-2" aria-label={t('engagementChain.locked')} />
        <bdi>{text || '—'}</bdi>
      </p>
    </Field>
  );
}

/**
 * D-316 — the levels the screen fixed, in ONE line at the top of a "new" form:
 * "🔒 נפתחת תחת: לקוח › סוג › התקשרות › אבן דרך". The same texts as the
 * `LockedValue` rows it replaces (used with `<EngagementChainFields hideLocked />`).
 */
export function LockedChainStrip({ chain }: { chain: EngagementChain }) {
  const { t } = useTranslation();
  const { value, lock } = chain;
  if (chain.hidden && !chain.companyMode) return null;
  const selected = chain.selectedEngagement;
  const milestoneText = value.milestoneId ? (chain.milestones.find((m) => m.id === value.milestoneId)?.name ?? '') : t('engagementChain.noMilestone');
  const parts: Array<{ key: string; label: string; text: string; tag?: boolean }> = [];
  if (!chain.hidden && isLocked('organizationId', lock)) parts.push({ key: 'customer', label: t('engagementChain.customer'), text: chain.organizationName });
  if (chain.companyMode && chain.internalWork) {
    if (chain.companyLocked) {
      const name = chain.internalProject?.name
        ?? (value.engagementId === chain.internalWork.engagementId ? t('engagementChain.directlyUnderOte') : (selected?.child_name ?? ''));
      parts.push({ key: 'engagement', label: t('engagementChain.internalProject'), text: name });
    }
    if (chain.showMilestone && isLocked('milestoneId', lock)) parts.push({ key: 'milestone', label: t('engagementChain.internalMilestone'), text: milestoneText });
  } else if (!chain.companyMode) {
    if (chain.withType && isLocked('engagementType', lock)) {
      parts.push({ key: 'type', label: t('engagementChain.type'), text: value.engagementType ? t(`engagementDetail.types.${value.engagementType}`) : '', tag: true });
    }
    if (isLocked('engagementId', lock)) {
      const engName = selected ? (chain.anyEngagement || !chain.withType
        ? engagementLabel(selected.child_name || t('engagementChain.unnamed'), selected.type, t)
        : (selected.child_name || t('engagementChain.unnamed'))) : '';
      parts.push({ key: 'engagement', label: t('engagementChain.engagement'), text: engName });
    }
    if (chain.showMilestone && isLocked('milestoneId', lock)) parts.push({ key: 'milestone', label: t('taskForm.milestone'), text: milestoneText });
  }
  if (parts.length === 0) return null;
  return (
    <p
      data-testid="locked-chain-strip"
      aria-readonly="true"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 text-sm text-text"
    >
      <Lock size={14} className="shrink-0 text-text-2" aria-label={t('engagementChain.locked')} />
      <span className="text-xs text-text-2">{t('engagementChain.openedUnder')}</span>
      {parts.map((p, i) => (
        <span key={p.key} className="inline-flex items-center gap-2">
          {i > 0 && <span aria-hidden="true" className="text-text-3">›</span>}
          <span className="sr-only">{p.label}: </span>
          <bdi title={p.label} className={p.tag ? 'rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-800' : 'font-medium'}>
            {p.text || '—'}
          </bdi>
        </span>
      ))}
    </p>
  );
}

export default function EngagementChainFields({ chain, idPrefix = 'task', errors = {}, onBlurField, onFieldChange, hideLocked = false }: {
  chain: EngagementChain;
  idPrefix?: string;
  /**
   * D-316 — leave the locked levels out (the form shows them in one line at
   * its top, `LockedChainStrip`). Off by default: every other caller unchanged.
   */
  hideLocked?: boolean;
  errors?: Record<string, string | false | null | undefined>;
  onBlurField?: (id: string) => void;
  /** Called with the control id after a user change (clear its error). */
  onFieldChange?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { value, onChange, lock } = chain;
  // P-33c: no customer / type / engagement for an internal-only employee — D-274 §3: but
  // on the task form she picks a project of "עבודה פנימית OTE" (optional) like everyone.
  if (chain.hidden && !chain.companyMode) return null;
  const ids = {
    customer: `${idPrefix}-customer`,
    type: `${idPrefix}-type`,
    engagement: `${idPrefix}-engagement`,
    milestone: `${idPrefix}-milestone`,
  };
  const setV = (field: keyof ChainValue, id: string) => (v: string) => {
    // P-33a (D-188) — "OTE, עבודה פנימית" picks the company and its engagement at once.
    onChange(field === 'organizationId'
      ? pickCustomer(value, v, chain.internalWork)
      : setChainField(value, field, v));
    onFieldChange?.(id);
  };
  const set = (field: keyof ChainValue, id: string) => (e: React.ChangeEvent<HTMLSelectElement>) => setV(field, id)(e.target.value);
  const engName = (e: ChainEngagement) => (chain.anyEngagement || !chain.withType
    ? engagementLabel(e.child_name || t('engagementChain.unnamed'), e.type, t)
    : (e.child_name || t('engagementChain.unnamed')));
  const selected = chain.selectedEngagement;

  // D-274 §3 — "פרויקט (לא חובה)": '' = "ישירות תחת OTE" ("עבודה פנימית").
  const lockedProjectName = chain.internalProject?.name
    ?? (value.engagementId === chain.internalWork?.engagementId ? t('engagementChain.directlyUnderOte') : (selected?.child_name ?? ''));
  const companyFields = chain.companyMode && chain.internalWork ? (
    <>
      {chain.companyLocked && hideLocked ? null : chain.companyLocked ? (
        <LockedValue id={ids.engagement} label={t('engagementChain.internalProject')} text={lockedProjectName} />
      ) : (
      <Field label={t('engagementChain.internalProject')} id={ids.engagement}>
        <EntityPicker
          icon={Link2}
          emptyLabel={t('engagementChain.directlyUnderOte')}
          options={chain.internalProjects.map((p) => ({ value: p.engagement_id, label: p.name }))}
          value={chain.internalProject?.engagement_id ?? ''}
          onChange={(v) => {
            onChange(pickInternalProject(chain.internalProjects.find((p) => p.engagement_id === v) ?? null, chain.internalWork!));
            onFieldChange?.(ids.engagement);
          }}
          loading={chain.internalProjectsLoading}
        />
      </Field>
      )}
      {chain.showMilestone && isLocked('milestoneId', lock) && !hideLocked && (
        <LockedValue
          id={ids.milestone}
          label={t('engagementChain.internalMilestone')}
          text={value.milestoneId ? (chain.milestones.find((m) => m.id === value.milestoneId)?.name ?? '') : t('engagementChain.noMilestone')}
        />
      )}
      {chain.showMilestone && !isLocked('milestoneId', lock) && (
        <Field label={t('engagementChain.internalMilestone')} id={ids.milestone}>
          <EntityPicker
            emptyLabel={t('engagementChain.noMilestone')}
            options={milestoneTree(chain.milestones).map(({ m, depth }) => ({ value: m.id, label: m.name, depth }))}
            value={value.milestoneId}
            onChange={setV('milestoneId', ids.milestone)}
          />
        </Field>
      )}
    </>
  ) : null;

  return (
    <>
      {chain.hidden ? null : isLocked('organizationId', lock) ? (hideLocked ? null :
        <LockedValue id={ids.customer} label={t('engagementChain.customer')} text={chain.organizationName} />
      ) : (
        <Field label={t('engagementChain.customer')} id={ids.customer} required error={errors[ids.customer] || undefined}>
          {/* P-41 (D-203) — searchable; a pre-filled customer that is not in the active list stays visible. */}
          <EntityPicker
            icon={Building2}
            placeholder={t('engagementChain.selectCustomer')}
            options={withCurrent(chain.organizations.map((o) => ({
              value: o.id, label: o.name,
              // D-274 §1 — "עבודה פנימית OTE" first, apart from the customers.
              ...(chain.internalWork && !o.isInternalWork ? { group: t(CUSTOMERS_GROUP_KEY) } : {}),
            })), value.organizationId, chain.organizationName)}
            value={value.organizationId}
            onChange={setV('organizationId', ids.customer)}
            onBlur={() => onBlurField?.(ids.customer)}
            loading={chain.orgsLoading}
          />
        </Field>
      )}

      {companyFields}

      {!chain.companyMode && chain.withType && (isLocked('engagementType', lock) ? (hideLocked ? null :
        <LockedValue id={ids.type} label={t('engagementChain.type')} text={value.engagementType ? t(`engagementDetail.types.${value.engagementType}`) : ''} />
      ) : (
        <Field
          label={t('engagementChain.type')}
          id={ids.type}
          required
          error={errors[ids.type] || undefined}
          hint={value.organizationId && !chain.engagementsLoading && chain.types.length === 0 ? t('engagementChain.noOpenEngagements') : undefined}
        >
          <select className="input" value={value.engagementType} onChange={set('engagementType', ids.type)} onBlur={() => onBlurField?.(ids.type)} disabled={!value.organizationId || chain.engagementsLoading}>
            <option value="">{value.organizationId ? t('engagementChain.selectType') : t('engagementChain.pickCustomerFirst')}</option>
            {chain.types.map((ty) => <option key={ty} value={ty}>{t(`engagementDetail.types.${ty}`)}</option>)}
          </select>
        </Field>
      ))}

      {chain.companyMode ? null : isLocked('engagementId', lock) ? (hideLocked ? null :
        <LockedValue id={ids.engagement} label={t('engagementChain.engagement')} text={selected ? engName(selected) : ''} />
      ) : (
        <Field
          label={t('engagementChain.engagement')}
          id={ids.engagement}
          required
          error={errors[ids.engagement] || undefined}
          hint={chain.anyEngagement && value.organizationId && !chain.engagementsLoading && chain.engagements.length === 0 ? t('engagementChain.noEngagements') : undefined}
        >
          <EntityPicker
            icon={Link2}
            placeholder={(chain.anyEngagement ? value.organizationId : value.engagementType) ? t('engagementChain.selectEngagement') : (chain.anyEngagement ? t('engagementChain.pickCustomerFirst') : t('engagementChain.pickTypeFirst'))}
            options={chain.engagements.map((e) => ({ value: e.id, label: engName(e) }))}
            value={value.engagementId}
            onChange={setV('engagementId', ids.engagement)}
            onBlur={() => onBlurField?.(ids.engagement)}
            disabled={chain.anyEngagement ? !value.organizationId || chain.engagementsLoading : !value.engagementType}
          />
        </Field>
      )}

      {!chain.companyMode && chain.showMilestone && (isLocked('milestoneId', lock) ? (hideLocked ? null :
        <LockedValue
          id={ids.milestone}
          label={t('taskForm.milestone')}
          text={value.milestoneId ? (chain.milestones.find((m) => m.id === value.milestoneId)?.name ?? '') : t('engagementChain.noMilestone')}
        />
      ) : (
        <Field
          label={t('taskForm.milestone')}
          id={ids.milestone}
          hint={!chain.milestonesLoading && chain.milestones.length === 0 ? t('taskForm.noMilestones') : undefined}
        >
          {/* P-06/A (D-57) — sub-milestones under their parent, indented. */}
          <EntityPicker
            emptyLabel={t('engagementChain.noMilestone')}
            options={milestoneTree(chain.milestones).map(({ m, depth }) => ({ value: m.id, label: m.name, depth }))}
            value={value.milestoneId}
            onChange={setV('milestoneId', ids.milestone)}
            loading={chain.milestonesLoading}
          />
        </Field>
      ))}
    </>
  );
}

export { milestoneTree };
